import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import {
  buildExtrasSalaryExport,
  type ExtrasSalaryEntry,
  type RawExtraSalaryRow,
} from "@/lib/extras-salary-calc";

export type { ExtrasSalaryEntry } from "@/lib/extras-salary-calc";

/**
 * Non-hook fetch for statistlønn-CSV-en (VIB-1283). Team-bred lesing av
 * project_extras på tvers av ALLE prosjekter (ingen .eq('project_id', …)) —
 * derfor fetchAllPaginated selv om tabellen i dag er liten (samme
 * føre-var-mønster som time_entries/invoices, jf. endringssjekk-skillen).
 * Ingen periode-filter: statister har ikke noe pålitelig datofelt å periodisere
 * på (kun created_at) og bølge 8-spec-en nevner ingen periodeavgrensning for
 * denne lista — eksporten er et øyeblikksbilde av gjeldende (ikke slettede)
 * statister, ikke en periodisert rapport som provisjon/lønnsgrunnlag.
 */
export async function fetchExtrasSalaryExport(): Promise<ExtrasSalaryEntry[]> {
  const rows = await fetchAllPaginated<RawExtraSalaryRow>(
    "project_extras",
    "id, project_id, full_name, name, personal_number, bank_account, agreed_amount, payment_type, projects:project_id(name, project_code)",
    (q) => q.is("deleted_at", null),
  );
  return buildExtrasSalaryExport(rows);
}

/**
 * Statistlønn-CSV-grunnlag (VIB-1283). `enabled` styres av kallstedet
 * (admin/superadmin-sjekk + feature-flag) så vi ikke spør DB-en for brukere
 * som uansett ikke skal se knappen.
 */
export function useExtrasSalaryExport(enabled: boolean) {
  return useQuery({
    queryKey: ["extras-salary-export"],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: fetchExtrasSalaryExport,
  });
}
